/**
 * PortalArticleReaderDialog — leitor de um Artigo de Conhecimento no portal
 * (E21.2.3, FR-15).
 *
 * Recebe o id (e o título da lista, pra mostrar de imediato) e busca o detalhe
 * com o conteúdo. O HTML já vem sanitizado do backend (bleach); DOMPurify é a
 * defesa-em-profundidade no render. Estados: loading / erro / preenchido.
 */
import DOMPurify from "dompurify";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
} from "lucide-react";

import {
  useConfirmReadArticle,
  usePortalArticleDetail,
} from "@/hooks/usePortalCentralFeed";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function formatDate(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export interface PortalArticleReaderDialogProps {
  articleId: string;
  fallbackTitle?: string;
  onClose: () => void;
}

export function PortalArticleReaderDialog({
  articleId,
  fallbackTitle,
  onClose,
}: PortalArticleReaderDialogProps) {
  const { data, isLoading, isError, refetch } = usePortalArticleDetail(articleId);
  const confirm = useConfirmReadArticle();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="portal-article-reader"
      >
        <DialogHeader>
          <DialogTitle>{data?.title ?? fallbackTitle ?? "Artigo"}</DialogTitle>
          <DialogDescription className="sr-only">
            Leitor de artigo da Base de Conhecimento.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando artigo…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 py-16 text-sm text-muted-foreground">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <span>Não foi possível abrir o artigo.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : data ? (
          <article className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">{data.category}</Badge>
              {data.sector_name && <span>{data.sector_name}</span>}
              {data.published_at && (
                <>
                  <span>•</span>
                  <span>{formatDate(data.published_at)}</span>
                </>
              )}
              {data.is_mandatory && (
                <Badge
                  variant={data.confirmed ? "outline" : "destructive"}
                  className="gap-1"
                  data-testid="portal-article-mandatory-badge"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Leitura obrigatória
                </Badge>
              )}
            </div>
            <div
              className="prose prose-sm max-w-none dark:prose-invert [&_img]:max-w-full [&_img]:rounded"
              data-testid="portal-article-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(data.content_html),
              }}
            />

            {data.is_mandatory && (
              <div
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                data-testid="portal-article-mandatory-footer"
              >
                {data.confirmed ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-500">
                    <CheckCircle2 className="h-4 w-4" />
                    Leitura confirmada
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                      {data.mandatory_deadline ? (
                        <>
                          <Clock className="h-4 w-4" />
                          Prazo: {formatDate(data.mandatory_deadline)}
                        </>
                      ) : (
                        "Confirme que você leu este artigo."
                      )}
                    </span>
                    <Button
                      size="sm"
                      disabled={confirm.isPending}
                      onClick={() => confirm.mutate(data.id)}
                      data-testid="portal-article-confirm-read"
                    >
                      {confirm.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Confirmar leitura
                    </Button>
                  </>
                )}
              </div>
            )}
          </article>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default PortalArticleReaderDialog;
